import React from "react";
import styles from "../../styles/dashboard.module.css";
import {getBooleanLabel} from "../utils";

export default function AlarmTableWidget({widgetName, device, variables}) {
    // Solo mostrar variables en estado warning o critical
    const activeAlarms = (variables || []).filter(v => v.high_active || v.medium_active);

    const getConsolidatedStatus = () => {
        if (activeAlarms.some(v => v.high_active)) return 'high';
        if (activeAlarms.some(v => v.medium_active)) return 'medium';
        return 'none';
    };

    const getVariableStatusIcon = (variable) => {
        if (variable.high_active) return {src: '/alerts/high.png', bg: '#FFE2E5', text: 'Danger'};
        if (variable.medium_active) return {src: '/alerts/medium.png', bg: '#FFF4DE', text: 'Warning'};
        return {src: '/alerts/low_disabled.png', bg: '#f8fafc', text: '--'};
    };

    const isBooleanValue = (value) => {
        const strValue = String(value).toLowerCase().trim();
        return ['true', 'false', '1', '0', '1.0', '0.0'].includes(strValue);
    };

    // Recibe la FILA, no el valor suelto: las etiquetas (ej. Abierto/Cerrado)
    // viven en la variable, no en el valor.
    const getBooleanDisplay = (variable) => {
        const {isTrue, label} = getBooleanLabel(variable);
        return {
            text: label,
            color: isTrue ? '#22c55e' : '#ef4444',
        };
    };

    const consolidatedStatus = getConsolidatedStatus();

    return (
        <div className={styles.variable_box} style={{
            padding: '12px 16px',
            height: '100%',
            maxHeight: 'none',
            width: '100% !important',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            {/* Header with consolidated semaphore */}
            <div className={'d-flex justify-content-between align-items-center mb-2'}>
                <div>
                    <div className={styles.variable_name} style={{fontSize: '16px'}}>
                        {widgetName || device?.name || 'Alarm Table'}
                    </div>
                    {device && (
                        <div style={{color: '#64748B', fontSize: '12px'}}>{device.name}</div>
                    )}
                </div>
                {/* Consolidated Semaphore */}
                <div className={'d-flex align-items-center'} style={{gap: '4px'}}>
                    <div style={{
                        width: 12, height: 12, borderRadius: '50%',
                        backgroundColor: consolidatedStatus === 'high' ? '#F43F5E' : '#E2E8F0',
                        border: consolidatedStatus === 'high' ? 'none' : '1px solid #CBD5E1',
                    }}/>
                    <div style={{
                        width: 12, height: 12, borderRadius: '50%',
                        backgroundColor: consolidatedStatus === 'medium' ? '#F59E0B' : '#E2E8F0',
                        border: consolidatedStatus === 'medium' ? 'none' : '1px solid #CBD5E1',
                    }}/>
                    <div style={{
                        width: 12, height: 12, borderRadius: '50%',
                        backgroundColor: consolidatedStatus === 'none' ? '#22C55E' : '#E2E8F0',
                        border: consolidatedStatus === 'none' ? 'none' : '1px solid #CBD5E1',
                    }}/>
                </div>
            </div>

            {/* Table */}
            <div style={{flex: 1, overflowY: 'auto', overflowX: 'hidden'}}>
                {activeAlarms.length > 0 ? (
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                        <thead>
                            <tr style={{borderBottom: '1px solid #E2E8F0'}}>
                                <th style={{textAlign: 'left', padding: '4px 8px', color: '#64748B', fontWeight: 500, fontSize: '11px'}}>Variable</th>
                                <th style={{textAlign: 'center', padding: '4px 8px', color: '#64748B', fontWeight: 500, fontSize: '11px'}}>Value</th>
                                <th style={{textAlign: 'center', padding: '4px 8px', color: '#64748B', fontWeight: 500, fontSize: '11px'}}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activeAlarms.map((variable, index) => {
                                const statusInfo = getVariableStatusIcon(variable);
                                const isBoolean = isBooleanValue(variable.value);
                                const boolDisplay = isBoolean ? getBooleanDisplay(variable) : null;

                                return (
                                    <tr key={variable.id || index} style={{borderBottom: '1px solid #F1F5F9'}}>
                                        <td style={{padding: '6px 8px', color: '#334155', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                            {variable.variable?.name || variable.name || `Var ${variable.id}`}
                                        </td>
                                        <td style={{textAlign: 'center', padding: '6px 8px'}}>
                                            {isBoolean ? (
                                                <div className={'d-flex align-items-center justify-content-center'} style={{gap: '4px'}}>
                                                    <div style={{
                                                        width: 8, height: 8, borderRadius: '50%',
                                                        backgroundColor: boolDisplay.color,
                                                        boxShadow: `0 0 4px ${boolDisplay.color}60`,
                                                    }}/>
                                                    <span style={{color: boolDisplay.color, fontWeight: 500, fontSize: '12px'}}>
                                                        {boolDisplay.text}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span style={{color: '#334155', fontWeight: 500}}>
                                                    {variable.value != null ? variable.value : '--'}
                                                    {variable.variable?.unit ? ` ${variable.variable.unit}` : ''}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{textAlign: 'center', padding: '6px 4px'}}>
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                backgroundColor: statusInfo.bg,
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                            }}>
                                                <img src={statusInfo.src} alt={statusInfo.text} style={{height: '14px'}}/>
                                                <span style={{fontSize: '11px', fontWeight: 500}}>{statusInfo.text}</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#22C55E',
                        fontSize: '14px',
                        fontWeight: 500,
                        gap: '8px',
                    }}>
                        <div style={{
                            width: 10, height: 10, borderRadius: '50%',
                            backgroundColor: '#22C55E',
                            boxShadow: '0 0 6px #22C55E60',
                        }}/>
                        All systems OK
                    </div>
                )}
            </div>
        </div>
    );
}
